import { ChangeDetectionStrategy, Component, inject, signal } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { FormField, Input, ZagButton } from '@zag/ui';
import { UsersMeService } from './users-me.service';

function passwordsMatchValidator(control: AbstractControl): ValidationErrors | null {
  const newPassword = control.get('newPassword');
  const confirmPassword = control.get('confirmPassword');
  if (!newPassword || !confirmPassword) return null;
  return newPassword.value === confirmPassword.value ? null : { passwordMismatch: true };
}

function passwordsDifferentValidator(control: AbstractControl): ValidationErrors | null {
  const currentPassword = control.get('currentPassword');
  const newPassword = control.get('newPassword');
  if (!currentPassword?.value || !newPassword?.value) return null;
  return currentPassword.value === newPassword.value ? { sameAsCurrentPassword: true } : null;
}

/**
 * Force-change-password page shown when `mustChangePassword = true`.
 *
 * Reached at `/auth/change-password` — the route is accessible only to
 * authenticated users (protected by `authGuard`). The `mustChangePasswordGuard`
 * redirects flagged users here; once the password is changed and the flag is
 * cleared, normal navigation resumes.
 *
 * Uses `UsersMeService.changePassword` (existing API; the user provides their
 * initial password as "current password" and picks a new one).
 *
 * Note: this page intentionally does NOT carry the `mustChangePasswordGuard`
 * itself — that would cause an infinite redirect loop.
 */
@Component({
  selector: 'sc-force-change-password',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, TranslatePipe, FormField, Input, ZagButton],
  template: `
    <div class="auth-container">
      <div class="auth-form">
        <h2 class="auth-header">
          <img src="/logo.svg" alt="Logo" />
          <span>{{ 'auth.forceChangePassword.title' | translate }}</span>
        </h2>

        <p class="auth-description">{{ 'auth.forceChangePassword.description' | translate }}</p>

        @if (errorMessage()) {
          <div class="error-message" role="alert">{{ errorMessage() }}</div>
        }

        <form [formGroup]="form" (ngSubmit)="onSubmit()" method="post">
          <div class="form-fields">
            <zag-form-field
              [label]="'auth.forceChangePassword.currentPassword' | translate"
              [error]="form.get('currentPassword')?.touched && form.get('currentPassword')?.invalid ? ('errors.required' | translate) : ''"
              [required]="true"
              forId="fcp-current-password"
            >
              <zag-input
                id="fcp-current-password"
                formControlName="currentPassword"
                type="password"
                [placeholder]="'auth.forceChangePassword.currentPasswordPlaceholder' | translate"
              ></zag-input>
            </zag-form-field>

            <zag-form-field
              [label]="'auth.forceChangePassword.newPassword' | translate"
              [error]="getNewPasswordError() | translate"
              [required]="true"
              forId="fcp-new-password"
            >
              <zag-input
                id="fcp-new-password"
                formControlName="newPassword"
                type="password"
                [placeholder]="'auth.forceChangePassword.newPasswordPlaceholder' | translate"
              ></zag-input>
            </zag-form-field>

            <zag-form-field
              [label]="'auth.forceChangePassword.confirmPassword' | translate"
              [error]="getConfirmPasswordError() | translate"
              [required]="true"
              forId="fcp-confirm-password"
            >
              <zag-input
                id="fcp-confirm-password"
                formControlName="confirmPassword"
                type="password"
                [placeholder]="'auth.forceChangePassword.confirmPasswordPlaceholder' | translate"
              ></zag-input>
            </zag-form-field>

            <button zagButton zagType="primary" type="submit" [disabled]="isLoading()" class="submit-button">
              {{ 'auth.forceChangePassword.submit' | translate }}
            </button>
          </div>
        </form>
      </div>
    </div>
  `,
  styles: [
    `
      .auth-container {
        width: 100%;
        max-width: 400px;
      }

      .auth-form {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
        width: 100%;
        max-width: 400px;
      }

      .form-fields {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-md);
      }

      .auth-header {
        display: flex;
        align-items: center;
        gap: var(--spacing-sm);
        margin-bottom: var(--spacing-md);
      }

      .auth-header img {
        width: 92px;
        height: auto;
      }

      .auth-description {
        margin: 0;
        color: var(--color-neutral-2);
        font-size: var(--font-size-sm);
      }

      .error-message {
        padding: var(--spacing-sm) var(--spacing-md);
        background-color: var(--color-error-bg);
        border: 1px solid var(--color-error-border);
        border-radius: var(--border-radius-sm);
        color: var(--color-error);
        font-size: var(--font-size-sm);
      }
    `,
  ],
})
export class ForceChangePasswordComponent {
  private readonly usersMeService = inject(UsersMeService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly translate = inject(TranslateService);

  protected readonly isLoading = signal(false);
  protected readonly errorMessage = signal<string | null>(null);

  protected readonly form = new FormGroup(
    {
      currentPassword: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
      newPassword: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.minLength(8)] }),
      confirmPassword: new FormControl('', { nonNullable: true, validators: [Validators.required] }),
    },
    { validators: [passwordsMatchValidator, passwordsDifferentValidator] }
  );

  protected getNewPasswordError(): string {
    const ctrl = this.form.get('newPassword');
    if (!ctrl?.touched) return '';
    if (ctrl.hasError('required')) return 'errors.required';
    if (ctrl.hasError('minlength')) return 'errors.passwordMinLength';
    if (this.form.hasError('sameAsCurrentPassword')) return 'errors.passwordSameAsCurrent';
    return '';
  }

  protected getConfirmPasswordError(): string {
    const ctrl = this.form.get('confirmPassword');
    if (!ctrl?.touched) return '';
    if (ctrl.hasError('required')) return 'errors.required';
    if (this.form.hasError('passwordMismatch')) return 'errors.passwordMismatch';
    return '';
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid || this.isLoading()) return;

    this.isLoading.set(true);
    this.errorMessage.set(null);

    const { currentPassword, newPassword } = this.form.getRawValue();

    try {
      await this.usersMeService.changePassword({ currentPassword, newPassword, revokeOtherSessions: false }).toPromise();
      this.usersMeService.reloadProfile();
      const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') ?? '/';
      await this.router.navigateByUrl(returnUrl);
    } catch {
      this.errorMessage.set(this.translate.instant('auth.forceChangePassword.error'));
    } finally {
      this.isLoading.set(false);
    }
  }
}
